import { useMemo } from "react";

import { VStack } from "@chakra-ui/react";
import { useFormSubmitErrors } from "@features/errors/form-errors.tsx";
import { useResendButton } from "@features/two-fa/hooks/use-resend-button";
import { zodResolver } from "@hookform/resolvers/zod";
import { ClientGateway } from "@kex/api";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { z } from "zod";

import { formatPhoneNumberDisplay } from "@shared/libs/phone-number";
import { otpCodeSchema } from "@shared/libs/validators";
import { useFormatErrorMessages } from "@shared/libs/validators/hooks/use-format-error-messages";
import { Button } from "@shared/ui/chakra/buttons/button";
import { InputField } from "@shared/ui/chakra/forms/input-field";
import { ResendButton } from "@shared/ui/molecules/resend-button";

import { otpFormMessages, transactionVerifyMessages } from "./messages";

export const otpSchema = z.object({
  code: otpCodeSchema,
});

export type OtpKind = ClientGateway.OtpDeliveryTypesEnum;

export type OtpFormValues = z.infer<typeof otpSchema>;

type Props = {
  deliveryContact: string;
  kind: OtpKind;
  resendBlockedUntil: Date | string;
  onResend: () => Promise<void>;
  onSubmit: (data: OtpFormValues) => Promise<void>;
};

export const OtpForm = ({
  deliveryContact,
  kind,
  resendBlockedUntil,
  onResend,
  onSubmit,
}: Props) => {
  const resendButtonProps = useResendButton({
    blockedUntil: resendBlockedUntil,
    onResend,
  });

  const {
    formState: { errors, isSubmitting, isValid },
    handleSubmit,
    register,
    reset,
  } = useForm<OtpFormValues>({
    defaultValues: {
      code: "",
    },
    mode: "onTouched",
    resolver: zodResolver(otpSchema),
  });
  const { formatMessage } = useIntl();

  const handleSubmitWithErrors = useFormSubmitErrors(
    onSubmit,
    ["ErrOtpExpired", "ErrOtpCodeMismatch", "ErrOtpInputAttemptsExceeded"],
    {
      ErrOtpCodeMismatch: () => reset(),
      ErrOtpExpired: () => reset(),
      ErrOtpInputAttemptsExceeded: () => reset(),
    },
  );
  const formatErrorMessage = useFormatErrorMessages();

  const label = useMemo(() => {
    const map: Record<OtpKind, string> = {
      [ClientGateway.OtpDeliveryTypesEnum.EMAIL]: formatMessage(
        otpFormMessages.emailLabel,
        { email: deliveryContact },
      ),
      [ClientGateway.OtpDeliveryTypesEnum.SMS]: formatMessage(
        otpFormMessages.smsLabel,
        { phone: formatPhoneNumberDisplay(deliveryContact) },
      ),
    };

    return map[kind];
  }, [formatMessage, deliveryContact, kind]);

  return (
    <form noValidate onSubmit={handleSubmit(handleSubmitWithErrors)}>
      <VStack alignItems="stretch" gap="4" w="full">
        <InputField
          errorText={formatErrorMessage(errors.code?.message)}
          input={{ ...register("code"), autoComplete: "off" }}
          inputBorderColor="bg.accent"
          label={label}
          labelColor="bg.accent"
          placeholder={formatMessage(
            transactionVerifyMessages.otpCodePlaceholder,
          )}
          isInvalid={Boolean(errors.code?.message)}
        />

        <ResendButton {...resendButtonProps} />

        <Button
          disabled={!isValid || isSubmitting}
          loading={isSubmitting}
          type="submit"
        >
          <FormattedMessage
            id="otp-form.submit-button"
            defaultMessage="Ввести"
          />
        </Button>
      </VStack>
    </form>
  );
};
